import { monitorForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { GenericBadgeV2, Icon } from "@studio3t/generic-components";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { ConfirmDeleteModal } from "@/components/ConfirmDeleteModal";
import { useJobs } from "@/hooks/useApi";
import { GroupSeparator, ReorderableStageGroup } from "./blockReorder";
import { jsonErrorRows } from "./editorErrors";
import { useInlineRename } from "./inlineRename";
import { NameModal } from "./NameModal";
import {
  ExplorerDropZone,
  type ExplorerResourceDragData,
  HandleBar,
  HandleSlot,
  isExplorerResourceDragData,
  LeadingIcon,
  RemoveRowButton,
  RowActionButton,
  RowActionsGroup,
  toggleActivationProps,
} from "./PipelineNavBar";
import {
  canStageAs,
  inputEditedSinceOpen,
  isDefaultUntitledName,
  isSchemaEditorId,
  isUntitledScriptId,
  pipelinesUsingResource,
  SINK_EDITOR_ID,
  SOURCE_EDITOR_ID,
  type StagedResource,
  type StagedSchema,
  type StageTabRole,
  schemaEditorId,
  schemaInstanceId,
  stageAcceptsDrop,
  TRANSFORM_EDITOR_ID,
  unattachedSchemaEditorId,
  usePipelineStaging,
} from "./pipelineStaging";
import { SaveScriptModal } from "./SaveScriptModal";
import { SchemaSubSection } from "./SchemaSubSection";
import { useSourceDelivers } from "./SourceDeliversDropdown";
import { useStudioPipeline } from "./StudioPipelineProvider";
import { scriptDraftErrorCount } from "./scriptErrors";
import { AddSquareIcon } from "./sectionIcons";
import {
  AccentBar,
  AlertSlot,
  ItemBody,
  ItemRow,
  PseudoIconWrap,
  PseudoText,
  RowBadgeSlot,
  RowName,
  StageAlert,
} from "./stageRowChrome";
import { isPendingOn } from "./stagingModel";
import { useScriptRun } from "./useScriptRun";
import { useScriptSaving } from "./useScriptSaving";

// semantic/greenbackground isn't exported by design-variables yet —
// inlined from the Figma token.

// The stage tabs sharing the Bridge_PipelineNavV2 layout: per-item
// groups with nested SCHEMA sub-sections. Sinks per 14397-191575 et al.,
// transforms (middlewares) per the MW variant 14419-217564, the source
// per the SRC variant 14397-174654.
export type StageNavRole = StageTabRole;

const STAGE_TEXTS = {
  // SOURCE is a single slot and takes no drop while it is taken
  // (stageAcceptsDrop): with one staged the pseudo-row invites the editor
  // only, and points at the remove for swapping the source.
  source: {
    editorRowId: SOURCE_EDITOR_ID,
    emptyText: "Drag a source from the explorer or use the editor",
    moreText: "Use the editor, or remove the staged source to drop another",
  },
  sink: {
    editorRowId: SINK_EDITOR_ID,
    emptyText: "Drag a sink from the explorer",
    moreText: "Drag another sink from the explorer",
  },
  transform: {
    editorRowId: TRANSFORM_EDITOR_ID,
    emptyText: "Drag a script from the explorer or use the editor",
    moreText: "Drag another script from the explorer or use the editor",
  },
} as const;

// The explorer drag currently in flight (payload data), or null. Drives the
// drag-revealed drop zones: the pseudo rows swap to a DropZone only while a
// compatible resource is being dragged (Figma 14397-193654).
export function useActiveExplorerDrag(): ExplorerResourceDragData | null {
  const [drag, setDrag] = useState<ExplorerResourceDragData | null>(null);
  useEffect(
    () =>
      monitorForElements({
        onDragStart: ({ source }) => {
          if (isExplorerResourceDragData(source.data)) setDrag(source.data);
        },
        onDrop: () => setDrag(null),
      }),
    [],
  );
  return drag;
}

const isStageDrag = (
  drag: ExplorerResourceDragData | null,
  role: StageNavRole,
) =>
  drag != null &&
  !drag.isSchema &&
  !drag.isTest &&
  canStageAs(role, drag.provider);

// A stage drag the tab can actually take right now: SOURCE turns its drop
// zone off while its slot is staged (the staged source has to be removed
// first — the Tests panel's one-suite rule), the other tabs always take.
const isStageDropDrag = (
  drag: ExplorerResourceDragData | null,
  role: StageNavRole,
  staged: StagedResource[],
) => isStageDrag(drag, role) && stageAcceptsDrop(role, staged);

const isSchemaDrag = (drag: ExplorerResourceDragData | null) =>
  drag != null && drag.isSchema === true;

const attachedTo = (schema: StagedSchema, role: StageNavRole, id: string) =>
  schema.attachments.some((a) => a.role === role && a.id === id);

// A tab's bottom block lists its pending schemas — "the next block's
// schema", independent of any attachments the schema also has elsewhere
// and of the other tabs' bottom blocks (isPendingOn, stagingModel).

// V2 stage-tab structure panel per Figma Bridge_PipelineNavV2 (sink: empty
// 14397-191575, dragging 14397-193654/-193674, staged 14397-193694;
// transform: MW variant 14419-217564; source: SRC empty 14397-174654,
// single slot): each staged item is a green-tinted
// group holding its row and its own nested SCHEMA sub-section; below the
// groups, a pseudo row invites the next drag, with one more SCHEMA
// sub-section for schemas not attached to any stage. Drop zones appear only
// while a compatible drag is running — stage items and schemas each land in
// their own zones.
// The prop is named `stage` (not `role`) so biome's ARIA-role lint doesn't
// read the literal at the call sites as an invalid ARIA role.
// Per-tab script flows: saving/renaming scripts that deployed pipelines
// use warns first (Continue / Save as), and the "Save as" fork + untitled
// draft share the script NameModal.
function useScriptFlows(role: StageNavRole) {
  const { scriptDrafts, effectiveSelectedId, setActiveSaveHandler } =
    usePipelineStaging();
  const scriptSaving = useScriptSaving();
  const { data: jobs } = useJobs();
  const [warnFor, setWarnFor] = useState<StagedResource | null>(null);
  const [renameWarnFor, setRenameWarnFor] = useState<StagedResource | null>(
    null,
  );
  const [saveAsFor, setSaveAsFor] = useState<StagedResource | null>(null);
  // An untitled draft's Save parks here for the Save-script modal — the
  // service choice (existing / brand-new) happens there.
  const [saveScriptFor, setSaveScriptFor] = useState<{
    item: StagedResource;
    suggestedName: string;
  } | null>(null);

  const usedByPipelines = (item: StagedResource) =>
    role === "transform" ? pipelinesUsingResource(jobs ?? [], item.id) : [];

  const onRowSave = (item: StagedResource) => {
    if (scriptDrafts[item.id] == null) return;
    if (usedByPipelines(item).length > 0) {
      setWarnFor(item);
      return;
    }
    scriptSaving.saveRow(item);
  };

  // Renaming an in-use script is refused by the engine — intercept the
  // inline edit and surface the modal with the fork path instead.
  const interceptRename = (item: StagedResource) => () => {
    if (usedByPipelines(item).length === 0) return false;
    setRenameWarnFor(item);
    return true;
  };

  const validateName = (name: string) =>
    scriptSaving.validateName(name)?.messages?.[0] ?? null;

  useScriptSaveHotkey({
    role,
    scriptDrafts,
    effectiveSelectedId,
    setActiveSaveHandler,
    onRowSave,
    openSaveModal: (item) =>
      setSaveScriptFor({ item, suggestedName: item.name }),
  });

  return {
    saving: scriptSaving,
    scriptDrafts,
    onRowSave,
    interceptRename,
    validateName,
    warnFor,
    setWarnFor,
    renameWarnFor,
    setRenameWarnFor,
    saveAsFor,
    setSaveAsFor,
    saveScriptFor,
    setSaveScriptFor,
  };
}

// The transform tab's rows — the only ones carrying script saves.
function useTransforms(role: StageNavRole) {
  const { transforms } = usePipelineStaging();
  return role === "transform" ? transforms : [];
}

// ⌘S while a transform row with edits is selected: an untitled draft
// opens the Save-script modal, a saved row runs the row save (in-use
// warning included). Registration is render-driven — the selected row's
// tab wins.
function useScriptSaveHotkey(options: {
  role: StageNavRole;
  scriptDrafts: Record<string, string>;
  effectiveSelectedId: string | null;
  setActiveSaveHandler: (handler: (() => void) | null) => void;
  onRowSave: (item: StagedResource) => void;
  openSaveModal: (item: StagedResource) => void;
}) {
  const { scriptDrafts, effectiveSelectedId, setActiveSaveHandler } = options;
  const selectedItem = useTransforms(options.role).find(
    (item) => item.id === effectiveSelectedId,
  );
  const selectedDraft =
    selectedItem != null ? scriptDrafts[selectedItem.id] : undefined;
  useEffect(() => {
    if (selectedItem == null || (selectedDraft ?? "").trim() === "") return;
    setActiveSaveHandler(() =>
      isUntitledScriptId(selectedItem.id)
        ? options.openSaveModal(selectedItem)
        : options.onRowSave(selectedItem),
    );
    return () => setActiveSaveHandler(null);
  });
}

type ScriptFlows = ReturnType<typeof useScriptFlows>;

// Unreferenced content in the bottom block: a pending schema no stage
// references at all, or the block's unsaved draft — Run would ignore
// either (Figma 14442-316243 / 14502-129720). A pending schema that's
// ALSO attached elsewhere does run, so it doesn't warn.
const hasUnreferencedContent = (pending: StagedSchema[], draft: string) =>
  pending.some((s) => s.attachments.length === 0) || draft !== "";

// The pseudo-row lights up when it holds the selection, and at rest while
// it's the only block on the tab — nothing staged yet — and nothing
// schema-ish holds the attention.
function pseudoRowHighlighted(options: {
  selectedId: string | null;
  editorRowId: string;
  restingEmpty: boolean;
  editorSourceId: string | null;
}): boolean {
  if (options.selectedId === options.editorRowId) return true;
  if (!options.restingEmpty) return false;
  if (isSchemaEditorId(options.selectedId)) return false;
  return options.editorSourceId === null;
}

// The bottom block tints while it holds the attention — a selection
// inside it or the resting empty-state highlight — matching the
// always-tinted stage groups.
function bottomBlockActive(options: {
  highlighted: boolean;
  selectedId: string | null;
  bottomRowId: string;
  pending: StagedSchema[];
}): boolean {
  if (options.highlighted) return true;
  if (options.selectedId === options.bottomRowId) return true;
  return options.pending.some(
    (s) => schemaInstanceId(s.id, options.bottomRowId) === options.selectedId,
  );
}

// The bottom block's derived display state: the tab's pending schemas,
// the unreferenced-content warning, and the pseudo-row/bottom tints.
function useBottomBlockState(role: StageNavRole, items: StagedResource[]) {
  const { schemas, schemaDrafts, effectiveSelectedId, editorSourceId } =
    usePipelineStaging();
  const texts = STAGE_TEXTS[role];
  const bottomRowId = unattachedSchemaEditorId(role);
  const pendingSchemas = schemas.filter((s) => isPendingOn(s, role));
  const warnUnreferenced = hasUnreferencedContent(
    pendingSchemas,
    schemaDrafts[bottomRowId] ?? "",
  );
  const pseudoHighlighted = pseudoRowHighlighted({
    selectedId: effectiveSelectedId,
    editorRowId: texts.editorRowId,
    // Untitled drafts sit in the items list, so they count as content.
    restingEmpty: items.length === 0 && pendingSchemas.length === 0,
    editorSourceId,
  });
  const bottomActive = bottomBlockActive({
    highlighted: pseudoHighlighted,
    selectedId: effectiveSelectedId,
    bottomRowId,
    pending: pendingSchemas,
  });
  return {
    texts,
    bottomRowId,
    pendingSchemas,
    warnUnreferenced,
    pseudoHighlighted,
    bottomActive,
  };
}

// A transform row's shared editing props — dirty flag, the EMPTY-script
// save gate, and the run action — apply the same way whether the row is a
// fresh untitled draft or an already-saved script.
function transformCoreProps(
  item: StagedResource,
  draftText: string | undefined,
  blockSchemas: StagedSchema[],
  scriptRun: ReturnType<typeof useScriptRun>,
) {
  return {
    dirty: draftText != null,
    // An EMPTY script never saves: an untitled draft without content,
    // or a saved row edited down to nothing.
    saveDisabled: draftText != null && draftText.trim() === "",
    onRun: () => scriptRun.runRow(item, blockSchemas[0]),
  };
}

// An untitled draft's own save/rename flavor: Save opens the Save-script
// modal (old /studio's SaveServiceModal) prefilled with the row's pending
// name — the script's home service (existing or brand-new) is chosen
// there. A rename is client-side only — nothing stored to move until
// Save names the file.
function untitledTransformProps(
  item: StagedResource,
  flows: ScriptFlows,
  renameTransformRow: ReturnType<
    typeof usePipelineStaging
  >["renameTransformRow"],
) {
  return {
    // The star marks the un-named default only — a custom-named draft
    // reads clean (Save still handles persisting).
    star: isDefaultUntitledName(item.name, "Untitled script"),
    onSaveWithName: async (name: string) => {
      flows.setSaveScriptFor({ item, suggestedName: name });
      return null;
    },
    onRename: async (name: string) => {
      renameTransformRow(item.id, { id: item.id, name });
      return true;
    },
    validateRename: flows.validateName,
  };
}

// A saved row's editing props: Save runs the in-use-pipeline warn flow,
// rename goes through the same warn interception.
function savedTransformProps(item: StagedResource, flows: ScriptFlows) {
  return {
    onSave: () => flows.onRowSave(item),
    onRename: (name: string) => flows.saving.renameRow(item, name),
    onRenameIntercept: flows.interceptRename(item),
    validateRename: flows.validateName,
  };
}

// The editing affordances — run, save, rename — exist on transform rows
// only; sink rows just select and unstage. An untitled draft is a
// transforms-list entry like any other — same position, same reorder
// handle — with its own save/discard flavor (see untitledTransformProps).
function transformRowProps(
  isTransform: boolean,
  isUntitled: boolean,
  item: StagedResource,
  draftText: string | undefined,
  blockSchemas: StagedSchema[],
  scriptRun: ReturnType<typeof useScriptRun>,
  flows: ScriptFlows,
  renameTransformRow: ReturnType<
    typeof usePipelineStaging
  >["renameTransformRow"],
) {
  if (!isTransform) return {};
  return {
    ...transformCoreProps(item, draftText, blockSchemas, scriptRun),
    ...(isUntitled
      ? untitledTransformProps(item, flows, renameTransformRow)
      : savedTransformProps(item, flows)),
  };
}

// The view-only studio always keeps a row selected: a second click on
// the selected row (which would hand the selection to the tab's hidden
// pseudo-row) changes nothing.
function useStageRowSelect() {
  const { effectiveSelectedId, toggleSelected } = usePipelineStaging();
  const { editsLocked } = useStudioPipeline();
  return (id: string) => {
    if (editsLocked && effectiveSelectedId === id) return;
    toggleSelected(id);
  };
}

// An edited input rides with the pipeline, not the source (Figma
// 14778-123822) — the pipeline runs the edited document, not the source's
// sample. The view-only studio edits nothing, and an input that arrived
// with the opened pipeline is not an edit (TTTL-191).
function useInputEditedOnSource(role: StageNavRole): boolean {
  const { inputDocument, inputOrigin, savedInput } = usePipelineStaging();
  const { editsLocked } = useStudioPipeline();
  return (
    !editsLocked &&
    role === "source" &&
    inputEditedSinceOpen(inputDocument, inputOrigin, savedInput)
  );
}

// A broken input document marks the source row itself with the red error
// icon (TTTL-215, Figma Bridge_PipelineNav 15496-289715) — the same error
// the deploy gate lists for the Source tab. Memoized so the parse runs
// per input change, not per stage-nav render.
function useInputErroredOnSource(role: StageNavRole): boolean {
  const { inputDocument } = usePipelineStaging();
  return useMemo(
    () => role === "source" && jsonErrorRows(inputDocument).length > 0,
    [role, inputDocument],
  );
}

// The badge an edited input puts right after the source row's name.
function inputEditedBadge(showInputEdited: boolean): ReactNode {
  if (!showInputEdited) return undefined;
  return (
    <RowBadgeSlot>
      <GenericBadgeV2
        content="input edited"
        color="info"
        emphasis="subtle"
        testId="input-edited-badge"
      />
    </RowBadgeSlot>
  );
}

// A stage block's own SCHEMA sub-section. The view-only studio shows it
// only when a schema is attached — an empty slot would only invite a drop
// that can't happen.
function StageBlockSchemas({
  role,
  item,
  drag,
  schemas,
  onToggleSelected,
}: {
  role: StageNavRole;
  item: StagedResource;
  drag: ExplorerResourceDragData | null;
  schemas: StagedSchema[];
  onToggleSelected: (id: string) => void;
}) {
  const { effectiveSelectedId, detachSchema, stageSchemaFor } =
    usePipelineStaging();
  const { editsLocked } = useStudioPipeline();
  if (editsLocked && schemas.length === 0) return null;
  return (
    <SchemaSubSection
      testId={`${role}-schema-${item.id}`}
      editorRowId={schemaEditorId(item.id)}
      schemas={schemas}
      schemaDragActive={isSchemaDrag(drag)}
      selectedId={effectiveSelectedId}
      onToggleSelected={onToggleSelected}
      // A schema can serve several stages — the group's remove only
      // detaches it from THIS one (the last detach unstages it).
      onUnstage={(schemaId) => detachSchema(schemaId, { role, id: item.id })}
      onDropSchema={(resource) =>
        stageSchemaFor(resource, { role, id: item.id })
      }
    />
  );
}

// One staged stage block: the item row (with the transform tab's run/save/
// rename affordances) and its SCHEMA sub-section, reorderable as a unit.
function StageBlock({
  role,
  item,
  drag,
  flows,
  scriptRun,
}: {
  role: StageNavRole;
  item: StagedResource;
  drag: ExplorerResourceDragData | null;
  flows: ScriptFlows;
  scriptRun: ReturnType<typeof useScriptRun>;
}) {
  const {
    schemas,
    effectiveSelectedId,
    unstage,
    scriptDrafts,
    discardScriptDraft,
    renameTransformRow,
    freshScriptDraftId,
    clearFreshScriptDraft,
    transforms,
  } = usePipelineStaging();
  const select = useStageRowSelect();
  const isTransform = role === "transform";
  // A lone transform has nowhere to go — the reorder handle (and the
  // schema indent that aligns under it) appears only with 2+ rows.
  const sortable = isTransform && transforms.length > 1;
  const isUntitled = isUntitledScriptId(item.id);
  const blockSchemas = schemas.filter((s) => attachedTo(s, role, item.id));
  // An edited input badges the source row; a broken one turns its icon red.
  const showInputEdited = useInputEditedOnSource(role);
  const inputErrored = useInputErroredOnSource(role);
  const draftText = scriptDrafts[item.id];
  // A script draft with errors turns its row's icon red too — the same
  // offline verdict the deploy gate lists (parsed per draft change).
  const scriptErrored = useMemo(
    () =>
      isTransform &&
      draftText != null &&
      scriptDraftErrorCount(item.id, draftText) > 0,
    [isTransform, item.id, draftText],
  );
  const transformProps = transformRowProps(
    isTransform,
    isUntitled,
    item,
    draftText,
    blockSchemas,
    scriptRun,
    flows,
    renameTransformRow,
  );
  return (
    <ReorderableStageGroup
      item={item}
      enabled={sortable}
      testId={`${role}-group`}
    >
      {(handleRef) => (
        <>
          <StagedItemRow
            role={role}
            item={item}
            selected={effectiveSelectedId === item.id}
            sortable={sortable}
            handleRef={handleRef}
            onToggleSelected={select}
            runDisabled={scriptRun.runningId === item.id}
            // The just-minted draft opens its name input right away —
            // focus stays in the code editor where the mint keystroke
            // is landing.
            startNaming={isUntitled && freshScriptDraftId === item.id}
            onStartNamingConsumed={clearFreshScriptDraft}
            // An untitled row's minus drops the draft with the row (the
            // draft entry is what makes it exist).
            onUnstage={(id) => {
              if (isUntitled) discardScriptDraft(id);
              unstage(id);
            }}
            badge={inputEditedBadge(showInputEdited)}
            errored={inputErrored || scriptErrored}
            {...transformProps}
          />
          <StageBlockSchemas
            role={role}
            item={item}
            drag={drag}
            schemas={blockSchemas}
            onToggleSelected={select}
          />
          <GroupSeparator />
        </>
      )}
    </ReorderableStageGroup>
  );
}

// The bottom block: while an Untitled script* draft exists it gets its own
// group — row plus the tab's unattached SCHEMA sub-section (the schemas it
// adopts on save) — and the "drag another / use the editor" pseudo-row
// keeps its own row after it (Figma 14419-273788); otherwise the pseudo-row
// heads the block holding the SCHEMA sub-section. One functional SCHEMA
// slot exists per tab, so it lives with whichever group is on screen last
// owning it.
function BottomBlock({
  role,
  items,
  drag,
  bottom,
}: {
  role: StageNavRole;
  items: StagedResource[];
  drag: ExplorerResourceDragData | null;
  bottom: ReturnType<typeof useBottomBlockState>;
}) {
  const {
    stage,
    stageSchema,
    stageDraftedSchema,
    removePending,
    effectiveSelectedId,
    toggleSelected,
  } = usePipelineStaging();
  return (
    <PseudoGroup $tinted={bottom.warnUnreferenced || bottom.bottomActive}>
      <StagePseudoRow
        role={role}
        empty={items.length === 0}
        highlighted={bottom.pseudoHighlighted}
        stageDragActive={isStageDropDrag(drag, role, items)}
        // A fresh cycle: selecting the pseudo-row clears the editor and
        // the next keystroke mints ANOTHER "Untitled script*" item (the
        // kept drafts stay as their own rows among the staged blocks).
        onToggleSelected={() => toggleSelected(bottom.texts.editorRowId)}
        onDropItem={(resource) => stage(role, resource)}
      />
      <SchemaSubSection
        testId={`${role}-schema-unattached`}
        editorRowId={bottom.bottomRowId}
        schemas={bottom.pendingSchemas}
        schemaDragActive={isSchemaDrag(drag)}
        selectedId={effectiveSelectedId}
        onToggleSelected={toggleSelected}
        // The bottom row's remove clears THIS tab's waiting state only;
        // the schema's attachments and other tabs' waiting copies stay.
        onUnstage={(schemaId) => removePending(schemaId, role)}
        onDropSchema={(resource) => stageSchema(resource, role)}
        // A "Save as" fork in the bottom block swaps the waiting copy:
        // the new schema goes pending, the old one's waiting state ends.
        onSaveAsReplaced={(schemaId) => removePending(schemaId, role)}
        // A drafted schema belongs to "the next stage" of this tab:
        // saving attaches it to the first schema-less one when it
        // already arrived.
        onSaveDraft={(resource) => stageDraftedSchema(resource, role)}
        unreferenced
      />
    </PseudoGroup>
  );
}

// The script NameModal ("Save as" fork only — untitled drafts save
// directly under their row name) and the two in-use warnings (save +
// rename), driven by the flows above.
// Wraps a save-flow promise into the modal's error-message contract.
async function reportSaveProblem(
  save: () => Promise<void>,
): Promise<string | null> {
  try {
    await save();
    return null;
  } catch (error) {
    return error instanceof Error ? error.message : "Failed to save script";
  }
}

function ScriptModals({ flows }: { flows: ScriptFlows }) {
  const saveTarget = flows.saveScriptFor;
  return (
    <>
      {/* A brand-new script picks its home service here (old /studio's
          SaveServiceModal, restyled). */}
      <SaveScriptModal
        isOpen={saveTarget !== null}
        script={
          saveTarget ? (flows.scriptDrafts[saveTarget.item.id] ?? "") : ""
        }
        suggestedName={saveTarget?.suggestedName ?? ""}
        onClose={() => flows.setSaveScriptFor(null)}
        onSaveExisting={(serviceName, name) =>
          reportSaveProblem(() =>
            flows.saving.saveNewInto({
              untitledId: saveTarget!.item.id,
              serviceName,
              name,
            }),
          )
        }
        onSaveNewService={(serviceName, name) =>
          reportSaveProblem(() =>
            flows.saving.saveIntoNewService({
              untitledId: saveTarget!.item.id,
              serviceName,
              name,
            }),
          )
        }
      />

      {/* Names the "Save as" fork of an in-use script's edits — the fork
          swaps into the staged row immediately under its new name. */}
      <NameModal
        isOpen={flows.saveAsFor !== null}
        onClose={() => {
          flows.setSaveAsFor(null);
        }}
        onSave={(name) => flows.saving.saveRowAs(flows.saveAsFor!, name)}
        title="Save script"
        placeholder="Add a name to the script file"
        emptyMessage="Add a name to the script"
        errorFallback="Failed to save script"
        testIdBase="save-script"
        validateName={flows.saving.validateName}
      />

      {/* Saving edits to a script that deployed pipelines run changes all
          of them — Continue saves in place, Save as forks the edits into a
          new file and swaps it into this row only. Same treatment as the
          shared-schema save warning. */}
      <ConfirmDeleteModal
        isOpen={flows.warnFor !== null}
        title="Save script"
        message={
          <StageAlert
            state="Warning"
            icon
            dismissible={false}
            width="100%"
            message={`"${flows.warnFor?.name ?? ""}" is used by existing pipelines — saving these changes will change what they run.`}
          />
        }
        confirmLabel="Continue"
        confirmType="primary"
        loadingLabel="Saving…"
        extraAction={{
          label: "Save as",
          onClick: () => {
            if (flows.warnFor) flows.setSaveAsFor(flows.warnFor);
            flows.setWarnFor(null);
          },
        }}
        onConfirm={() => {
          if (flows.warnFor) flows.saving.saveRow(flows.warnFor);
          flows.setWarnFor(null);
        }}
        onCancel={() => flows.setWarnFor(null)}
      />

      {/* Renaming a script that deployed pipelines run is refused by the
          engine — surface it up front and offer the fork path instead. */}
      <ConfirmDeleteModal
        isOpen={flows.renameWarnFor !== null}
        title="Rename script"
        message={
          <StageAlert
            state="Warning"
            icon
            dismissible={false}
            width="100%"
            message={`"${flows.renameWarnFor?.name ?? ""}" is used by existing pipelines and cannot be renamed — you can save a copy under a new name instead.`}
          />
        }
        confirmLabel="Save as"
        confirmType="primary"
        loadingLabel="Saving…"
        onConfirm={() => {
          if (flows.renameWarnFor) flows.setSaveAsFor(flows.renameWarnFor);
          flows.setRenameWarnFor(null);
        }}
        onCancel={() => flows.setRenameWarnFor(null)}
      />
    </>
  );
}

// SOURCE is a single block: with a source staged there is no next block,
// so the bottom invitation and its unattached SCHEMA slot leave the
// screen. They come back while the block still holds content — a waiting
// schema or an unsaved draft must stay visible. A source drag brings
// nothing back: the taken slot offers no drop zone (stageAcceptsDrop),
// so the staged source has to be removed before another can land.
function showsBottomBlock(options: {
  role: StageNavRole;
  items: StagedResource[];
  bottom: ReturnType<typeof useBottomBlockState>;
  drag: ExplorerResourceDragData | null;
  viewOnly: boolean;
}): boolean {
  const { role, items, bottom, drag, viewOnly } = options;
  // The view-only studio takes no drops and drafts nothing — the
  // invitation rows and the unattached SCHEMA slot have nothing to offer.
  if (viewOnly) return false;
  if (role !== "source") return true;
  if (items.length === 0) return true;
  if (bottom.pendingSchemas.length > 0) return true;
  return bottom.warnUnreferenced || isStageDropDrag(drag, role, items);
}

// The panel's trailing alert slots, each shown only when its block is in
// the state it describes.
function PanelAlerts({
  inputEditedAlert,
  avroAlert,
  unreferencedAlert,
  codeIssues,
}: {
  inputEditedAlert: boolean;
  /** The source delivers Avro with no schema attached (TTTL-268): the
   *  engine cannot decode a record. The text the Source delivers state
   *  produces, under the schema slot like the unreferenced-schema alert. */
  avroAlert: string | null;
  unreferencedAlert: boolean;
  /** View-only studio: what the pipeline code couldn't be shown as
   *  stages for (pipelineStagesFromCode). */
  codeIssues: string[];
}) {
  return (
    <>
      {codeIssues.length > 0 && (
        <AlertSlot>
          <StageAlert
            state="Error"
            icon
            dismissible={false}
            width="100%"
            message={`The pipeline code can't fully be shown as stages — see Full pipeline. ${codeIssues.join("; ")}`}
            testId="code-issues-alert"
          />
        </AlertSlot>
      )}
      {/* An edited input rides with the pipeline, not the source — the
          info alert under the source group says so (Figma 14778-123822). */}
      {inputEditedAlert && (
        <AlertSlot>
          <StageAlert
            state="Info"
            icon
            dismissible={false}
            width="100%"
            message="The input was edited and will be saved within this pipeline"
            testId="input-edited-alert"
          />
        </AlertSlot>
      )}

      {avroAlert && (
        <AlertSlot>
          <StageAlert
            state="Warning"
            icon
            dismissible={false}
            width="100%"
            message={avroAlert}
            testId="source-avro-alert"
          />
        </AlertSlot>
      )}

      {/* An unreferenced schema warns right under its block (Figma
          14442-316243's Alert slot) — Run would ignore it. */}
      {unreferencedAlert && (
        <AlertSlot>
          <StageAlert
            state="Warning"
            icon
            dismissible={false}
            // The lib default is fit-content — the design's alert fills
            // its padded slot.
            width="100%"
            message="Schema will not be applied on run because it is not referenced in the pipeline."
            testId="schema-unattached-alert"
          />
        </AlertSlot>
      )}
    </>
  );
}

// The tab's staged blocks, in staging order.
function StageBlocks({
  role,
  items,
  drag,
  flows,
  scriptRun,
}: {
  role: StageNavRole;
  items: StagedResource[];
  drag: ExplorerResourceDragData | null;
  flows: ScriptFlows;
  scriptRun: ReturnType<typeof useScriptRun>;
}) {
  return (
    <>
      {items.map((item) => (
        <StageBlock
          key={item.id}
          role={role}
          item={item}
          drag={drag}
          flows={flows}
          scriptRun={scriptRun}
        />
      ))}
    </>
  );
}

// A row of this tab: a stage row, or a schema instance in a stage row's
// block (schemaInstanceId keys those on the block's editor row).
const isTabRow = (item: StagedResource, selectedId: string | null) =>
  item.id === selectedId ||
  (selectedId?.startsWith("schema:") === true &&
    selectedId.endsWith(`|${schemaEditorId(item.id)}`));

// The view-only studio always shows a selection on the tab: with the
// selection elsewhere (another tab's row, nothing at all), the tab's
// first row takes it — its content lands in the editor like a click.
function useViewOnlyTabSelection(items: StagedResource[]) {
  const { effectiveSelectedId, toggleSelected } = usePipelineStaging();
  const { editsLocked } = useStudioPipeline();
  useEffect(() => {
    if (!editsLocked || items.length === 0) return;
    const owned = items.some((item) => isTabRow(item, effectiveSelectedId));
    if (!owned) toggleSelected(items[0].id);
  }, [editsLocked, items, effectiveSelectedId, toggleSelected]);
}

export function StageNavPanel({ stage: role }: { stage: StageNavRole }) {
  const { sources, sinks, transforms, codeIssues } = usePipelineStaging();
  const { editsLocked } = useStudioPipeline();
  const drag = useActiveExplorerDrag();
  const items = { source: sources, transform: transforms, sink: sinks }[role];
  useViewOnlyTabSelection(items);
  // The trailing info alert needs a staged source under it as well.
  const inputEditedOnSource = useInputEditedOnSource(role);
  const flows = useScriptFlows(role);
  const scriptRun = useScriptRun();
  const bottom = useBottomBlockState(role, items);
  // Avro with no schema on the source: the Source tab's alert (the
  // footer control only picks the encoding).
  const delivers = useSourceDelivers();
  const avroAlert =
    role === "source" && delivers?.state.kind === "editable"
      ? delivers.state.warning
      : null;
  const blockProps = { role, items, drag, flows, scriptRun };

  return (
    <Panel data-testid={`${role}-nav-panel`}>
      <StageBlocks {...blockProps} />

      {showsBottomBlock({
        role,
        items,
        bottom,
        drag,
        viewOnly: editsLocked,
      }) && <BottomBlock {...blockProps} bottom={bottom} />}

      <ScriptModals flows={flows} />

      <PanelAlerts
        inputEditedAlert={inputEditedOnSource && items.length > 0}
        avroAlert={avroAlert}
        unreferencedAlert={bottom.warnUnreferenced}
        codeIssues={codeIssues}
      />
    </Panel>
  );
}

// The row's trailing Run/Save/Remove group — Run and Save render only
// when the tab wires them (transform rows).
function StagedRowActions({
  item,
  onRun,
  runDisabled,
  onSave,
  saveDisabled,
  onUnstage,
}: {
  item: StagedResource;
  onRun?: () => void;
  runDisabled?: boolean;
  onSave?: () => void;
  saveDisabled: boolean;
  onUnstage: (id: string) => void;
}) {
  return (
    <RowActionsGroup>
      {onRun && (
        <RowActionButton
          label={`Run ${item.name}`}
          icon={Icon.Run}
          viewSafe
          onClick={onRun}
          disabled={runDisabled}
        />
      )}
      {onSave && (
        <RowActionButton
          label={`Save ${item.name}`}
          icon={Icon.Save}
          onClick={onSave}
          disabled={saveDisabled}
        />
      )}
      <RemoveRowButton name={item.name} onRemove={() => onUnstage(item.id)} />
    </RowActionsGroup>
  );
}

// The three-bar reorder handle slot — mounted only while the block is
// sortable (it also carries the enclosing group's drag-handle ref, so an
// unmounted slot means no handle to grab).
function TransformHandleSlot({
  sortable,
  handleRef,
}: {
  sortable?: boolean;
  handleRef?: React.RefObject<HTMLSpanElement>;
}) {
  if (!sortable) return null;
  return (
    <HandleSlot ref={handleRef} $sortable aria-hidden>
      <HandleBar />
      <HandleBar />
      <HandleBar />
    </HandleSlot>
  );
}

// The row's name slot: the inline rename field/label once the row is
// renamable, the plain name otherwise — a trailing asterisk marks dirty
// content (star overrides dirty for the untitled-draft's un-named-only
// marker).
function rowNameNode(options: {
  renamable: boolean;
  rename: ReturnType<typeof useInlineRename>;
  name: string;
  badge?: ReactNode;
  star?: boolean;
  dirty: boolean;
}): ReactNode {
  if (options.renamable) return options.rename.nameNode;
  return (
    <RowName $fit={options.badge != null}>
      {options.name}
      {(options.star ?? options.dirty) ? "*" : ""}
    </RowName>
  );
}

// The row's inline double-click rename wiring: clicking into a rename
// also selects the row first, so the editor content it loads matches what
// is being renamed.
function useRowRename(options: {
  item: StagedResource;
  dirty: boolean;
  star?: boolean;
  selected: boolean;
  onToggleSelected: (id: string) => void;
  onRename?: (name: string) => Promise<boolean>;
  validateRename?: (name: string) => string | null;
  onRenameIntercept?: () => boolean;
  startNaming: boolean;
}) {
  return useInlineRename({
    name: options.item.name,
    dirty: options.dirty,
    star: options.star,
    testId: "transform-rename-input",
    onRename: options.onRename ?? (async () => false),
    validateRename: options.validateRename ?? (() => null),
    interceptEdit: options.onRenameIntercept,
    // Switching rows closes an open name input; a still-un-named
    // untitled row (star doubles as that flag) re-opens it on the next
    // selection — saved rows only open via double-click.
    selected: options.selected,
    reopenOnSelect: options.star ?? false,
    ensureSelected: () => {
      if (!options.selected) options.onToggleSelected(options.item.id);
    },
    // The keystroke that minted the draft is mid-flight in the code
    // editor — the open input must not steal it (startEditing opens
    // hands-off).
    startEditing: options.startNaming,
  });
}

// Consumes the fresh-mint flag so a later remount (tab switch) shows the
// plain label again.
function useConsumeStartNaming(startNaming: boolean, onConsumed?: () => void) {
  useEffect(() => {
    if (startNaming) onConsumed?.();
  }, [startNaming, onConsumed]);
}

// Direct save (untitled rows) under the row's pending name; a rejection
// opens the name input red with the message.
function useDirectSave(
  onSaveWithName: ((name: string) => Promise<string | null>) | undefined,
  rename: ReturnType<typeof useInlineRename>,
) {
  const [directSaving, setDirectSaving] = useState(false);
  const saveWithName = async () => {
    if (!onSaveWithName || directSaving) return;
    setDirectSaving(true);
    const message = await onSaveWithName(rename.pendingName);
    setDirectSaving(false);
    if (message) rename.openWithError(message);
  };
  return { directSaving, saveWithName };
}

// Staged stage-item row (Figma 14442-316243 "crimes" / 14419-218773
// "Transform_bla_bla.rhai"): 50px section/item holding a 40px List/item —
// default-tint leading icon, textheading name, trailing remove. Selectable
// like the other rows: a selected transform loads its script into the
// editor and a selected source its sample document (toggleSelected's
// content load); a sink owns no editor, the selection just moves.
// Transform rows edit like schema rows: a dirty
// asterisk while the editor text diverges from the stored script, a Save
// action, and an inline double-click rename.
function StagedItemRow({
  role,
  item,
  selected,
  dirty = false,
  star,
  badge,
  errored = false,
  onToggleSelected,
  onRun,
  runDisabled,
  onSave,
  onSaveWithName,
  saveDisabled = false,
  onUnstage,
  onRename,
  validateRename,
  onRenameIntercept,
  startNaming = false,
  onStartNamingConsumed,
  sortable,
  handleRef,
}: {
  role: StageNavRole;
  item: StagedResource;
  selected: boolean;
  dirty?: boolean;
  /** Label-star override (untitled drafts: star only while un-named). */
  star?: boolean;
  /** Status badge right after the name (source row's "input edited"). */
  badge?: ReactNode;
  /** The row's document holds an error — the leading icon goes red
   *  (TTTL-215). */
  errored?: boolean;
  onToggleSelected: (id: string) => void;
  /** Runs the row's script (Figma 14419-273788) — transform rows only. */
  onRun?: () => void;
  runDisabled?: boolean;
  /** Persists the row's script edits — transform rows only. */
  onSave?: () => void;
  /** An untitled row's DIRECT save under the row's pending name (no
   *  modal); resolves an error message to show red on the name input,
   *  or null on success. */
  onSaveWithName?: (name: string) => Promise<string | null>;
  /** Extra Save gate on top of the rename/dirty rules — an EMPTY script
   *  never saves. */
  saveDisabled?: boolean;
  onUnstage: (id: string) => void;
  /** Persists a new filename — transform rows only. */
  onRename?: (name: string) => Promise<boolean>;
  validateRename?: (name: string) => string | null;
  /** Pre-rename gate: true = swallowed (a modal took over). */
  onRenameIntercept?: () => boolean;
  /** A just-minted untitled draft opens its name input on mount WITHOUT
   *  taking focus — the mint happened mid-typing in the code editor. */
  startNaming?: boolean;
  onStartNamingConsumed?: () => void;
  /** Renders the three-bar drag handle (block reorder) — transform rows. */
  sortable?: boolean;
  /** Mounts the reorder drag handle for the enclosing group's draggable. */
  handleRef?: React.RefObject<HTMLSpanElement>;
}) {
  const renamable = onRename !== undefined;
  const rename = useRowRename({
    item,
    dirty,
    star,
    selected,
    onToggleSelected,
    onRename,
    validateRename,
    onRenameIntercept,
    startNaming,
  });
  useConsumeStartNaming(startNaming, onStartNamingConsumed);
  const { directSaving, saveWithName } = useDirectSave(onSaveWithName, rename);
  return (
    <ItemRow
      data-testid={`pipeline-${role}-item`}
      data-resource-id={item.id}
      $active={selected}
      $interactive
      {...toggleActivationProps(() => onToggleSelected(item.id))}
    >
      <ItemBody>
        <TransformHandleSlot sortable={sortable} handleRef={handleRef} />
        <LeadingIcon icon={item.icon} active={selected} errored={errored} />
        {rowNameNode({
          renamable,
          rename,
          name: item.name,
          badge,
          star,
          dirty,
        })}
        {badge}
      </ItemBody>
      <StagedRowActions
        item={item}
        onRun={onRun}
        runDisabled={runDisabled}
        onSave={onSaveWithName ? saveWithName : onSave}
        saveDisabled={saveDisabled || rename.saveDisabled || directSaving}
        onUnstage={onUnstage}
      />
      {selected && <AccentBar />}
    </ItemRow>
  );
}

// The "Drag a/another …" pseudo row. Selectable like the SCHEMA
// placeholder — on the sink tab it owns no editor (the panel keeps the
// no-editor message); on the transform tab it's the script editor's "use
// the editor" row (a fresh entry resets the editor to its templates zero
// state, the existing TRANSFORM_EDITOR_ID semantics) and on the source
// tab the input document editor's (SOURCE_EDITOR_ID). Selecting it also
// drops a selected schema row or tree pill. While a compatible drag runs,
// its content swaps to the inline drop zone (row keeps the tint + accent
// in the empty state, per 14397-193654).
function StagePseudoRow({
  role,
  empty,
  highlighted,
  stageDragActive,
  onToggleSelected,
  onDropItem,
}: {
  role: StageNavRole;
  empty: boolean;
  highlighted: boolean;
  stageDragActive: boolean;
  onToggleSelected: () => void;
  onDropItem: (resource: StagedResource) => void;
}) {
  const texts = STAGE_TEXTS[role];
  if (stageDragActive) {
    return (
      <StageDropRow role={role} highlighted={highlighted} onDrop={onDropItem} />
    );
  }
  return (
    <ItemRow
      data-testid={`${role}-pseudo-row`}
      $active={highlighted}
      $interactive
      {...toggleActivationProps(onToggleSelected)}
    >
      <ItemBody>
        <PseudoIconWrap $emphasis={highlighted}>
          <AddSquareIcon />
        </PseudoIconWrap>
        <PseudoText $emphasis={highlighted}>
          {empty ? texts.emptyText : texts.moreText}
        </PseudoText>
      </ItemBody>
      {highlighted && <AccentBar />}
    </ItemRow>
  );
}

// The pseudo-row's drag-active face: the inline drop zone for a
// compatible stage resource (row keeps the tint + accent in the empty
// state, per 14397-193654).
function StageDropRow({
  role,
  highlighted,
  onDrop,
}: {
  role: StageNavRole;
  highlighted: boolean;
  onDrop: (resource: StagedResource) => void;
}) {
  return (
    <ItemRow data-testid={`${role}-pseudo-row`} $active={highlighted} $zone>
      <ExplorerDropZone
        testId={`${role}-drop-zone`}
        shownDuringDrag
        canDrop={(data) =>
          !data.isSchema && !data.isTest && canStageAs(role, data.provider)
        }
        onDrop={({ id, name, icon, provider }) =>
          onDrop({ id, name, icon, provider })
        }
      />
      {highlighted && <AccentBar />}
    </ItemRow>
  );
}

// No gap between the groups (Figma 14397-199487): each stage group closes
// with its own separator and the next group starts flush against it.
const Panel = styled.div`
  display: flex;
  flex-direction: column;
`;

// The bottom block gets the same group tint as the stage groups once it
// holds a staged (unattached) schema — Figma 14442-316243 — or while the
// selection sits inside it; the idle empty variant (14397-199487) stays
// untinted.
const GREEN_BACKGROUND = "rgba(26, 198, 115, 0.05)";

const PseudoGroup = styled.div<{ $tinted?: boolean }>`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  background: ${(p) => (p.$tinted ? GREEN_BACKGROUND : "transparent")};
`;
